import styled from '@emotion/styled';
import classNames from 'classnames';
import { Ban } from 'lucide-react';
import { ReactNode } from 'react';

import { theme } from 'styles/theme/theme';
import { UDText } from 'ud-ui/components';
import overrideSelectComponents from './override';
import * as S from './styles';

/**
 * Пресет компонентов react-select для асинхронного поиска.
 *
 * Отличия от обычного `override` — те, что нужны именно поиску: подсветка строки под
 * курсором, скролл в длинной выдаче, «Загрузка...» на время запроса и «Нет совпадений»
 * вместо «Список пуст» (список не пуст — просто ничего не нашлось).
 *
 * `SingleValue` здесь намеренно не переопределён: выбранное значение всегда рисует
 * доменный компонент (команда — с логотипом, игрок — ФИО), и передаёт его вызывающая
 * сторона через `components`.
 */

const Option = styled(S.Option)`
  transition: background-color 0.25s ease;

  &:hover {
    background-color: ${({ theme }) => theme.colors.AccentCornsilk};
  }

  /* Недоступную опцию не подсвечиваем: подсветка читается как «сейчас выберется». */
  &[data-disabled='true'],
  &[data-disabled='true']:hover {
    cursor: not-allowed;
    background-color: transparent;
  }
`;

/**
 * Подпись вместо выдачи: «Загрузка...», «Нет совпадений» и то, что вызывающая сторона
 * покажет вместо «Нет совпадений» при пустом вводе (`emptyInputMessage` у `UDAsyncSelect`).
 */
export const AsyncMessage = ({ children }: { children: ReactNode }) => {
  return (
    <div className="flex-center flex-col p-8">
      <UDText type="subhead" className="text-surfaceRomanSilver30 mb-2">
        {children}
      </UDText>
    </div>
  );
};

export const LoadingMessage = () => {
  return (
    <UDText type="subhead" color={theme.colors.WhiteGray}>
      Загрузка...
    </UDText>
  );
};

const asyncSelectComponents = {
  ...overrideSelectComponents,
  /**
   * Отключённая опция (`option.isDisabled`) отличается от обычной видом, а не только тем,
   * что не выбирается: приглушённый текст и перечёркнутый маркер вместо чекбокса — иначе
   * пустая галочка выглядит как «ещё не выбрано» и по ней жмут.
   */
  Option: (props: any) => {
    const className = classNames('flex items-center justify-between gap-3 px-4 w-full', props.isMulti ? 'py-1' : 'py-2');

    return (
      <Option
        {...props.innerProps}
        className={className}
        data-disabled={Boolean(props.isDisabled)}
        style={{
          width: '100%',
          maxWidth: '100%',
          overflow: 'hidden',
        }}
      >
        <UDText type="subhead" className={classNames('w-full', props.isDisabled && 'text-surfaceRomanSilver30')}>
          {props.children}
        </UDText>
        {props.isMulti &&
          (props.isDisabled ? (
            <Ban size={24} className="text-surfaceRomanSilver30 shrink-0" data-testid="select-option-disabled" />
          ) : (
            <S.OptionCheckbox data-testid="select-option-checkbox" data-checked={props.isSelected} />
          ))}
      </Option>
    );
  },
  MenuList: (props: any) => {
    const { cx, ...restProps } = props;
    const { innerProps } = props;
    return (
      <S.MenuList
        {...restProps}
        {...innerProps}
        className="scrollbar-rounded"
        style={{
          maxHeight: '222px',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        {props.children}
      </S.MenuList>
    );
  },
  NoOptionsMessage: () => <AsyncMessage>Нет совпадений</AsyncMessage>,
};

export default asyncSelectComponents;
